import { Tables } from "@/lib/supabase/supabase_types_override";
import { evaluateQuestionConditionals } from "@/lib/utils/query/question/evaluateQuestionsConditionals";
import { QuestionWithConditionResult } from "../hooks/useQuery/useGetQuestionsBySurveyId";
import { extractAllDependencies } from "../hooks/useQuery/useGetQuestionsBySurveyId/utils/groupQuestionsByDependencies";


export function getQuestionsInvalidatedByResponse(
    questionId: Tables<'question'>['id'],
    newValue: string,
    questions: Array<QuestionWithConditionResult>,
    userId: string
): Array<QuestionWithConditionResult> {
    console.group(`🎯 Simple traverse invalidation for ${questionId} = "${newValue}"`);

    // Step 1: Find directly invalidated questions using existing evaluation
    const questionsWithNewEvaluation = evaluateQuestionConditionals(
        questionId,
        newValue,
        questions,
        userId
    );

    const directlyInvalidated: string[] = [];

    for (const originalQuestion of questions) {
        if (originalQuestion.id === questionId) continue;

        const questionWithNewEval = questionsWithNewEvaluation.find(q => q.id === originalQuestion.id);
        if (!questionWithNewEval) continue;

        const wasValid = originalQuestion.condition_met === true;
        const willBeInvalid = questionWithNewEval.condition_met === false;

        if (wasValid && willBeInvalid) {
            console.log(`📉 Directly invalidated: ${originalQuestion.question}`);
            directlyInvalidated.push(originalQuestion.id);
        }
    }

    // Step 2: Traverse down from each invalidated question to find all dependents
    const allInvalidatedIds = new Set<string>();

    function traverseDown(questionId: string) {
        if (allInvalidatedIds.has(questionId)) return; // Already processed

        allInvalidatedIds.add(questionId);
        console.log(`🔽 Adding to invalidated: ${questions.find(q => q.id === questionId)?.question}`);

        // Find all questions that depend on this question and are currently valid
        const dependents = questions.filter(q => {
            // Skip if not currently valid (no point invalidating something already invalid)
            if (q.condition_met !== true) return false;

            // Skip if already in invalidated set
            if (allInvalidatedIds.has(q.id)) return false;

            // Check if this question depends on the invalidated question
            const dependencies = extractAllDependencies(
                q.selected_configuration?.conditionals?.where
            );

            return dependencies.includes(questionId);
        });

        // Recursively traverse down each dependent
        dependents.forEach(dependent => {
            console.log(`  🔗 ${dependent.question} depends on invalidated question`);
            traverseDown(dependent.id);
        });
    }

    // Start traversal from each directly invalidated question
    directlyInvalidated.forEach(invalidatedId => {
        traverseDown(invalidatedId);
    });

    // Convert IDs back to question objects
    const allInvalidatedQuestions = Array.from(allInvalidatedIds)
        .map(id => questions.find(q => q.id === id))
        .filter(q => q !== undefined) as QuestionWithConditionResult[];

    console.log(`\n📊 Total questions to invalidate: ${allInvalidatedQuestions.length}`);
    allInvalidatedQuestions.forEach(q => console.log(`  - ${q.question}`));

    console.groupEnd();
    return allInvalidatedQuestions;
}